import { SearchInput } from "@octopusdeploy/design-system-components";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { DeploymentTargetResource, EnvironmentResource, ProjectResource, Repository, KubernetesLiveStatusResponse, KubernetesLiveStatusResourceObject, GetLiveStatusRollupBffResponse, TimelineItem } from "@octopusdeploy/octopus-server-client";
import { TaskState } from "@octopusdeploy/octopus-server-client";
import { isEqual } from "lodash";
import { useEffect, useMemo, useState } from "react";
import * as React from "react";
import type { ViewLiveStatusPageEvent } from "~/analytics/Analytics";
import { useAnalyticLiveStatusPageDispatch } from "~/analytics/Analytics";
import type { LiveObjectStatusFilter } from "~/areas/projects/components/Observability/LiveStatusFiltering";
import { LiveStatusFiltering, defaultFilter, filterMachineResources } from "~/areas/projects/components/Observability/LiveStatusFiltering";
import { LiveStatusTable } from "~/areas/projects/components/Observability/LiveStatusTable";
import { LiveStatusObjectDrawer } from "~/areas/projects/components/Observability/ObjectDrawer/LiveStatusObjectDrawer";
import { DeploymentTimelineDrawer } from "~/areas/projects/components/Releases/Deployments/DeploymentTimelineDrawer";
import type { ProjectContextProps } from "~/areas/projects/context/index";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { NotFound } from "~/components/NotFound/NotFound";
import { PageContent } from "~/components/PageContent/PageContent";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { LiveStatusEarlyAccessChip } from "../Projects/LiveStatusSwitch";
import { LiveStatusFeedbackCallout } from "./LiveStatusFeedbackCallout";
import { LiveStatusSummaryBar } from "./LiveStatusSummaryBar";
interface LoaderData {
    liveStatusRollup: GetLiveStatusRollupBffResponse;
    latestDeployment: TimelineItem;
    environment: EnvironmentResource;
    kubernetesLiveStatus: KubernetesLiveStatusResponse;
    deploymentTargets: DeploymentTargetResource[];
}
type LiveStatusPageProps = {
    loaderData: LoaderData;
    project: ProjectResource;
    tenantId?: string;
};
const refetchIntervalInMs = 1000;
export function LiveStatusPage({ loaderData, project, tenantId }: LiveStatusPageProps) {
    const [filter, setFilter] = useState<LiveObjectStatusFilter>(defaultFilter);
    const [resourceInDrawer, setResourceInDrawer] = useState<KubernetesLiveStatusResourceObject | undefined>(undefined);
    const [isTimelineDrawerOpen, setIsTimelineDrawerOpen] = useState<boolean>(false);
    const featureToggleEnabled = useOctopusFeatureToggle("kubernetes-monitor", false) && isFeatureToggleEnabled("KubernetesLiveObjectStatusFeatureToggle");
    const { result: { liveStatusRollup, latestDeployment, kubernetesLiveStatus, deploymentTargets }, } = useQuery(async (repository) => refresh(repository, project.Id, loaderData.environment.Id, tenantId), [project.Id, loaderData.environment.Id, tenantId], "Live Status Page", {
        initialResult: loaderData,
        refetchIntervalInMs,
    });
    const resourcesToRender = filterMachineResources(kubernetesLiveStatus.MachineStatuses, filter);
    function updateFilterContent<K extends keyof LiveObjectStatusFilter>(updates: Pick<LiveObjectStatusFilter, K>) {
        setFilter({ ...defaultFilter, ...updates });
    }
    const showTimelineDrawer = () => setIsTimelineDrawerOpen(true);
    const additionalKindTypes = useMemo(() => [...new Set(kubernetesLiveStatus.MachineStatuses.flatMap((m) => m.Resources.map((r) => r.Kind)))], [kubernetesLiveStatus.MachineStatuses]);
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const namespaceNames: string[] = useMemo(() => [...new Set(kubernetesLiveStatus.MachineStatuses.flatMap((m) => m.Resources.map((r) => r.Namespace as string)).filter((ns) => !!ns))], [kubernetesLiveStatus.MachineStatuses]);
    return (<>
            {!featureToggleEnabled && <NotFound />}
            {featureToggleEnabled && (<>
                    <PageContent header={{
                title: `${project.Name} ${loaderData.environment.Name} Live Status`,
                chip: <LiveStatusEarlyAccessChip />,
                pageActions: [{ type: "button", buttonType: "secondary", label: "Timeline", onClick: () => showTimelineDrawer() }],
            }} description={<LiveStatusFeedbackCallout />} filters={{
                inputs: [<SearchInput autoFocus={true} value={filter.name} onChange={(x) => updateFilterContent({ name: x })} accessibleName={"search by object name"} placeholder={"Search by object name..."}/>],
                advancedFilters: {
                    onResetFilter: () => setFilter(defaultFilter),
                    hasUserSelectedValues: !isEqual(defaultFilter, filter),
                    content: <LiveStatusFiltering filter={filter} setFilter={setFilter} additionalKindTypes={additionalKindTypes} namespaceNames={namespaceNames}/>,
                },
            }}>
                        <LiveStatusSummaryBar spaceId={project.SpaceId} projectSlug={project.Slug} liveStatus={liveStatusRollup} latestDeployment={latestDeployment}/>
                        <LiveStatusTable spaceId={project.SpaceId} deploymentTargets={deploymentTargets} machineLiveStatusResources={resourcesToRender} resourceSelected={(resource) => setResourceInDrawer(resource)}/>
                    </PageContent>
                    <LiveStatusObjectDrawer environmentId={loaderData.environment.Id} projectId={project.Id} tenantId={tenantId} onClose={() => setResourceInDrawer(undefined)} resource={resourceInDrawer} latestDeployment={latestDeployment} analyticEventData={{
                releaseId: latestDeployment.ReleaseId,
                deploymentStatus: latestDeployment.TaskState,
                liveApplicationHealth: liveStatusRollup.Status,
                parentPage: "KLOS",
                executionType: "deployment",
            }}/>
                    <DeploymentTimelineDrawer isOpen={isTimelineDrawerOpen} onClose={() => setIsTimelineDrawerOpen(false)} deploymentName={project.Name} environmentId={loaderData.environment.Id} projectId={project.Id} spaceId={project.SpaceId} tenantId={tenantId}/>
                    <LiveStatusPageAnalyticView eventData={{
                projectId: project.Id,
                releaseId: latestDeployment.ReleaseId,
                environmentId: loaderData.environment.Id,
                deploymentId: latestDeployment.Id,
                deploymentStatus: latestDeployment.TaskState,
                liveApplicationHealth: liveStatusRollup.Status,
            }}/>
                </>)}
        </>);
}
export async function liveStatusPageLoader(repository: Repository, environmentId: string, projectContext: Promise<ProjectContextProps>, tenantId?: string): Promise<LoaderData> {
    const { state: { model: project }, } = await projectContext;
    const refreshingData = await refresh(repository, project.Id, environmentId, tenantId);
    return {
        ...refreshingData,
        environment: await repository.Environments.get(environmentId),
    };
}
async function refresh(repository: Repository, projectId: string, environmentId: string, tenantId?: string) {
    // There was more complicated logic ensuring that tenantId was null before, I can't reproduce the issue so I'm leaving this here to ensure that I haven't missed an edge case
    const nullableTenantId: string | null = tenantId === undefined ? null : tenantId;
    const liveStatusRollupPromise = repository.KubernetesLiveStatus.getRollupForApplicationInstance(projectId, environmentId, tenantId);
    const latestDeploymentPromise = getLatestTimelineItem(repository, projectId, environmentId, nullableTenantId);
    const kubernetesLiveStatus = await repository.KubernetesLiveStatus.getForApplicationInstance(projectId, environmentId, tenantId);
    return {
        liveStatusRollup: await liveStatusRollupPromise,
        latestDeployment: await latestDeploymentPromise,
        kubernetesLiveStatus,
        deploymentTargets: await repository.Machines.all({ ids: kubernetesLiveStatus.MachineStatuses.map((r) => r.MachineId) }),
    };
}
async function getLatestTimelineItem(repository: Repository, projectId: string, environmentId: string, tenantId: string | null) {
    const deploymentsTimeline = await repository.Deployments.getDeploymentTimeline(projectId, environmentId, tenantId || undefined);
    const latestForEnv = deploymentsTimeline.TimelineItems.Items.filter((d) => d.EnvironmentId === environmentId && d.TaskState !== TaskState.Queued);
    const latestForTenant = latestForEnv.filter((d) => d.TenantId === tenantId);
    return latestForTenant[0];
}
function LiveStatusPageAnalyticView({ eventData }: {
    eventData: ViewLiveStatusPageEvent;
}) {
    const dispatch = useAnalyticLiveStatusPageDispatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => dispatch("View Live Status Page", eventData), []);
    return <></>;
}
